import React from 'react';
import { colors, spacing, typography } from '../../../design-system/index.js';

export const PortfolioGallery = () => {
  const sectionStyles = {
    padding: `${spacing[16]} 0`,
    borderBottom: `1px solid ${colors.border.light}`,
  };

  const titleStyles = {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.light,
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing[12],
    margin: `0 0 ${spacing[12]} 0`,
  };

  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: spacing[8],
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const cardStyles = {
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.base,
    border: `1px solid ${colors.border.default}`,
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    opacity: 0,
    animation: 'fadeInUp 0.6s ease forwards',
  };

  const imageContainerStyles = {
    height: '200px',
    backgroundColor: colors.primary[300],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: typography.fontSize.lg,
    color: colors.text.muted,
    position: 'relative',
    overflow: 'hidden',
  };

  const overlayStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    color: colors.text.primary,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.normal,
  };

  const contentStyles = {
    padding: spacing[6],
  };

  const projectTitleStyles = {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.normal,
    color: colors.text.primary,
    marginBottom: spacing[3],
    margin: `0 0 ${spacing[3]} 0`,
  };

  const projectDescStyles = {
    fontSize: typography.fontSize.base,
    lineHeight: typography.lineHeight.loose,
    color: colors.text.secondary,
    marginBottom: spacing[4],
    margin: `0 0 ${spacing[4]} 0`,
  };

  const tagsContainerStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing[2],
    marginBottom: spacing[4],
  };

  const tagStyles = {
    fontSize: typography.fontSize.xs,
    padding: `${spacing[1]} ${spacing[3]}`,
    backgroundColor: colors.background.tertiary,
    color: colors.text.primary,
    borderRadius: spacing.borderRadius.full,
    border: `1px solid ${colors.border.default}`,
  };

  const linksContainerStyles = {
    display: 'flex',
    gap: spacing[3],
  };

  const linkStyles = {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    textDecoration: 'none',
    padding: `${spacing[2]} ${spacing[4]}`,
    border: `1px solid ${colors.border.default}`,
    borderRadius: spacing.borderRadius.base,
    transition: 'all 0.3s ease',
  };

  const portfolioData = [
    {
      title: "企业级设计系统",
      description: "基于 Figma API 构建的现代化设计系统，包含完整的 Design Tokens 和可复用组件库。",
      tags: ["React", "Figma API", "Design System", "TypeScript"],
      image: "设计系统截图",
      demoUrl: "#",
      codeUrl: "#"
    },
    {
      title: "AI 驱动的内容平台", 
      description: "利用机器学习算法个性化推荐内容，支持多媒体格式，用户参与度提升 200%。",
      tags: ["Vue.js", "Python", "AI/ML", "Redis"],
      image: "内容平台截图",
      demoUrl: "#",
      codeUrl: "#"
    },
    {
      title: "实时协作工具",
      description: "支持多人在线协作的文档编辑工具，实现实时同步和版本控制功能。",
      tags: ["React", "WebSocket", "Node.js", "MongoDB"],
      image: "协作工具截图", 
      demoUrl: "#",
      codeUrl: "#"
    },
    {
      title: "移动端电商应用",
      description: "原生移动应用，包含完整的购物流程、支付集成和用户评价系统。",
      tags: ["React Native", "Redux", "Firebase", "Stripe"],
      image: "电商应用截图",
      demoUrl: "#",
      codeUrl: "#"
    },
    {
      title: "数据可视化仪表板",
      description: "企业级数据分析平台，支持多种图表类型和实时数据更新。",
      tags: ["D3.js", "React", "WebGL", "PostgreSQL"],
      image: "仪表板截图",
      demoUrl: "#", 
      codeUrl: "#"
    },
    {
      title: "开源组件库",
      description: "轻量级、可定制的 React 组件库，已获得 1000+ GitHub stars。",
      tags: ["React", "Storybook", "Jest", "Rollup"],
      image: "组件库截图",
      demoUrl: "#",
      codeUrl: "#"
    }
  ];

  const handleCardMouseEnter = (e) => {
    e.currentTarget.style.transform = 'translateY(-5px)';
    e.currentTarget.style.boxShadow = spacing.shadow.lg;
    
    const overlay = e.currentTarget.querySelector('.image-overlay');
    if (overlay) {
      overlay.style.opacity = '1';
    }
  };

  const handleCardMouseLeave = (e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = 'none';
    
    const overlay = e.currentTarget.querySelector('.image-overlay');
    if (overlay) {
      overlay.style.opacity = '0';
    }
  };

  const handleLinkMouseEnter = (e) => {
    e.target.style.backgroundColor = colors.background.tertiary;
    e.target.style.borderColor = colors.text.primary;
  };

  const handleLinkMouseLeave = (e) => {
    e.target.style.backgroundColor = 'transparent';
    e.target.style.borderColor = colors.border.default;
  };

  return (
    <section style={sectionStyles} className="portfolio-gallery">
      <h2 style={titleStyles}>作品展示</h2>
      
      <div style={gridStyles} className="portfolio-grid">
        {portfolioData.map((project, index) => (
          <div
            key={index}
            style={{
              ...cardStyles,
              animationDelay: `${index * 0.1}s`
            }}
            className="portfolio-card"
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
          >
            <div style={imageContainerStyles}>
              <span>{project.image}</span>
              <div style={overlayStyles} className="image-overlay">
                点击查看详情
              </div>
            </div>
            
            <div style={contentStyles}>
              <h3 style={projectTitleStyles}>{project.title}</h3>
              <p style={projectDescStyles}>{project.description}</p>
              
              <div style={tagsContainerStyles}>
                {project.tags.map((tag, tagIndex) => (
                  <span key={tagIndex} style={tagStyles}>
                    {tag}
                  </span>
                ))}
              </div>
              
              <div style={linksContainerStyles}>
                <a 
                  href={project.demoUrl} 
                  style={linkStyles}
                  onMouseEnter={handleLinkMouseEnter}
                  onMouseLeave={handleLinkMouseLeave}
                >
                  预览
                </a>
                <a 
                  href={project.codeUrl} 
                  style={linkStyles}
                  onMouseEnter={handleLinkMouseEnter}
                  onMouseLeave={handleLinkMouseLeave}
                >
                  代码
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};
